import {
  Box,
  Typography,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  type ProtocolTemplateWithDetails,
  type FollowUpScheduleRecord,
  type EscalationStrategyWithActions,
} from '../../../utils/loadDataFromContract';

interface ProtocolTemplatesTabProps {
  protocolTemplates: ProtocolTemplateWithDetails[];
}

export const ProtocolTemplatesTab = ({ protocolTemplates }: ProtocolTemplatesTabProps) => (
  <>
    <Alert severity="info" sx={{ mb: 3 }}>
      Pre-built protocol templates based on condition type. Use as foundation and personalize with CGM data and patient preferences.
    </Alert>

    <Grid container spacing={3}>
      {protocolTemplates.map((template) => (
        <Grid item xs={12} key={template.id}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="h6">{template.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.description}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Target Population
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {template.targetPopulation}
                  </Typography>

                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Glucose Targets
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary={`Fasting: ${template.glucoseTargets.fasting}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={`Post-Meal: ${template.glucoseTargets.postMeal}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={`Bedtime: ${template.glucoseTargets.bedtime}`} />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Meal Guidelines
                  </Typography>
                  <List dense>
                    {(template.mealGuidelines as string[]).map((guideline, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={guideline} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                    Timing Rules
                  </Typography>
                  <List dense>
                    {(template.timingRules as string[]).map((rule, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={rule} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Button variant="outlined" size="small">
                    Use This Template
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}
    </Grid>
  </>
);

interface FollowUpSchedulesTabProps {
  followUpSchedules: FollowUpScheduleRecord[];
}

export const FollowUpSchedulesTab = ({ followUpSchedules }: FollowUpSchedulesTabProps) => (
  <>
    <Alert severity="info" sx={{ mb: 3 }}>
      Standard follow-up schedule for nutritionist care coordination. Adjust based on patient acuity and response.
    </Alert>

    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Phase</TableCell>
            <TableCell>Timeline</TableCell>
            <TableCell>Visit Frequency</TableCell>
            <TableCell>Focus Areas</TableCell>
            <TableCell>Deliverables</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {followUpSchedules.map((schedule, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography variant="subtitle2">{schedule.phase}</Typography>
              </TableCell>
              <TableCell>{schedule.weeks}</TableCell>
              <TableCell>{schedule.visits}</TableCell>
              <TableCell>{schedule.focus}</TableCell>
              <TableCell>{schedule.deliverables}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Visit Documentation Requirements
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Every Visit
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="CGM data review (7-14 days)" /></ListItem>
              <ListItem><ListItemText primary="Meal log review" /></ListItem>
              <ListItem><ListItemText primary="Adherence assessment" /></ListItem>
              <ListItem><ListItemText primary="Hypoglycemia screening" /></ListItem>
              <ListItem><ListItemText primary="Patient questions addressed" /></ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Quarterly (Minimum)
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="A1C review" /></ListItem>
              <ListItem><ListItemText primary="Weight trend analysis" /></ListItem>
              <ListItem><ListItemText primary="Protocol effectiveness review" /></ListItem>
              <ListItem><ListItemText primary="Gap matrix update" /></ListItem>
              <ListItem><ListItemText primary="PCP coordination note" /></ListItem>
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </>
);

interface EscalationStrategiesTabProps {
  escalationStrategies: EscalationStrategyWithActions[];
}

export const EscalationStrategiesTab = ({ escalationStrategies }: EscalationStrategiesTabProps) => (
  <>
    <Alert severity="warning" sx={{ mb: 3 }}>
      <strong>Critical:</strong> Escalation triggers require timely action to prevent adverse outcomes.
      Document all escalations in patient chart and notify care team.
    </Alert>

    <Grid container spacing={3}>
      {escalationStrategies.map((strategy, index) => (
        <Grid item xs={12} key={index}>
          <Card
            sx={{
              borderLeft: 4,
              borderColor:
                strategy.urgency === 'EMERGENCY'
                  ? 'error.main'
                  : strategy.urgency === 'URGENT'
                  ? 'error.light'
                  : strategy.urgency === 'HIGH'
                  ? 'warning.main'
                  : 'info.main',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6">{strategy.trigger}</Typography>
                <Chip
                  label={strategy.urgency}
                  size="small"
                  color={
                    strategy.urgency === 'EMERGENCY' || strategy.urgency === 'URGENT'
                      ? 'error'
                      : strategy.urgency === 'HIGH'
                      ? 'warning'
                      : 'info'
                  }
                />
              </Box>

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Trigger Criteria
              </Typography>
              <Typography variant="body2" paragraph>
                {strategy.criteria}
              </Typography>

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Required Actions
              </Typography>
              <List dense>
                {(strategy.actions as string[]).map((action, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={action}
                      primaryTypographyProps={{
                        variant: 'body2',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Escalation Workflow
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label="1. Identify Trigger" color="info" />
          <Chip label="2. Assess Severity" color="info" />
          <Chip label="3. Implement Actions" color="info" />
          <Chip label="4. Document in Chart" color="info" />
          <Chip label="5. Notify Care Team" color="info" />
          <Chip label="6. Follow-Up Within 48hrs" color="info" />
        </Box>
      </CardContent>
    </Card>
  </>
);
